import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import { ChevronDown, ChevronUp, Calculator, Mail, Building2, RefreshCcw, ExternalLink } from "lucide-react";

const USD = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function AdminDemos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | with_roi | without_roi
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/demo");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError("Failed to load demo requests.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = items.filter((d) => {
    if (filter === "with_roi" && !d.roi_model) return false;
    if (filter === "without_roi" && d.roi_model) return false;
    if (search) {
      const q = search.toLowerCase();
      const blob = [d.first_name, d.last_name, d.work_email, d.clinic_name, d.clinic_type]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  });

  const totalWithRoi = items.filter((d) => d.roi_model).length;

  return (
    <div data-testid="admin-demos-page">
      <Section className="pt-28 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-3">
              Internal · Admin
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Demo requests
            </h1>
            <p className="mt-3 text-slate-600 max-w-xl">
              Inbound demo signups, with attached ROI calculator models when carried over from the website.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/vendors"
              className="text-xs font-medium text-slate-500 hover:text-slate-900"
            >
              ↗ Vendor pipeline
            </Link>
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Stat row */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat label="Total requests" value={items.length} testId="admin-stat-total" />
          <Stat label="With ROI model" value={totalWithRoi} testId="admin-stat-with-roi" />
          <Stat
            label="ROI carry-over rate"
            value={items.length ? `${Math.round((totalWithRoi / items.length) * 100)}%` : "—"}
            testId="admin-stat-rate"
          />
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, clinic…"
            data-testid="admin-search"
            className="flex-1 min-w-[240px] bg-white border border-slate-200 rounded-full px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 text-xs font-semibold">
            {[
              { id: "all", label: "All" },
              { id: "with_roi", label: "With ROI" },
              { id: "without_roi", label: "Without ROI" },
            ].map((f) => (
              <button
                type="button"
                key={f.id}
                data-testid={`admin-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-3.5 py-1.5 rounded-full transition-colors ${
                  filter === f.id ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}

        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-slate-500">No demo requests yet.</p>
          </div>
        )}

        <div className="space-y-3">
          {filtered.map((d) => {
            const open = openId === d.id;
            const roi = d.roi_model;
            return (
              <div
                key={d.id}
                data-testid={`admin-demo-row-${d.id}`}
                className="rounded-2xl border border-slate-200 bg-white"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : d.id)}
                  data-testid={`admin-demo-toggle-${d.id}`}
                  className="w-full text-left flex items-center gap-4 px-5 py-4 hover:bg-slate-50/60 rounded-2xl transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                    <Building2 className="w-4 h-4 text-slate-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-slate-900 truncate">
                        {d.first_name} {d.last_name}
                      </div>
                      <span className="text-xs text-slate-500">·</span>
                      <span className="text-xs text-slate-600 truncate">{d.clinic_name}</span>
                      <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                        {d.clinic_type}
                      </span>
                      {roi && (
                        <span
                          data-testid={`admin-demo-roi-badge-${d.id}`}
                          className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.16em] font-bold text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5"
                        >
                          <Calculator className="w-3 h-3" /> ROI
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {d.work_email}
                      </span>
                      <span>·</span>
                      <span>{fmtDate(d.created_at)}</span>
                    </div>
                  </div>
                  {open ? (
                    <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                  )}
                </button>

                {open && (
                  <div
                    data-testid={`admin-demo-details-${d.id}`}
                    className="px-5 pb-5 pt-2 border-t border-slate-100"
                  >
                    <div className="grid lg:grid-cols-12 gap-6 mt-4">
                      <div className="lg:col-span-7 space-y-3 text-sm">
                        <KV k="Role" v={d.role || "—"} />
                        <KV k="Team size" v={d.team_size || "—"} />
                        <KV
                          k="Interested programs"
                          v={
                            d.interested_programs && d.interested_programs.length
                              ? d.interested_programs.join(", ")
                              : "—"
                          }
                        />
                        <div>
                          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400 mb-1">
                            Message
                          </div>
                          <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded-lg border border-slate-200 p-4 leading-relaxed">
                            {d.message || "—"}
                          </pre>
                        </div>
                      </div>

                      <div className="lg:col-span-5">
                        {roi ? (
                          <div
                            data-testid={`admin-demo-roi-${d.id}`}
                            className="rounded-2xl bg-slate-900 text-white p-5"
                          >
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
                              <Calculator className="w-3.5 h-3.5" /> ROI model
                            </div>
                            <div className="mt-4 space-y-2.5 text-sm">
                              <Row k="Conversations / mo" v={roi.conversations} />
                              <Row k="Conversion" v={`${roi.conversion_pct}%`} />
                              <Row k="Avg cash-pay price" v={USD(roi.price_usd)} />
                              <Row k="Vendor cost / svc" v={USD(roi.vendor_cost_usd)} muted />
                              <Row k="Platform cost / mo" v={USD(roi.platform_cost_usd)} muted />
                              <div className="border-t border-white/10 pt-3 mt-2 space-y-1.5">
                                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300">
                                  Net contribution
                                </div>
                                <div className="font-display text-2xl font-extrabold tracking-tighter tabular">
                                  {USD(roi.net_monthly_usd)}
                                  <span className="text-xs text-slate-400 font-medium ml-1.5">/ mo</span>
                                </div>
                                <div className="text-sm text-slate-300 tabular">
                                  {USD(roi.net_annualized_usd)} <span className="text-slate-400 text-xs">/ yr</span>
                                </div>
                              </div>
                            </div>
                            <p className="mt-4 text-[10.5px] text-slate-400 leading-relaxed">
                              Illustrative planning figures submitted by user. Not a forecast or commitment.
                            </p>
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-5 text-center">
                            <p className="text-sm text-slate-500">No ROI model attached to this submission.</p>
                            <p className="mt-1 text-[11px] text-slate-400">
                              User did not arrive via the ROI calculator CTA.
                            </p>
                          </div>
                        )}
                        <a
                          href={`mailto:${d.work_email}?subject=Re: Nodera Health platform demo`}
                          className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-blue-700"
                        >
                          Reply via email <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-900">
            ← Back to site
          </Link>
        </div>
      </Section>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-slate-900 tabular">{value}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">{k}</div>
      <div className="col-span-2 text-slate-700">{v}</div>
    </div>
  );
}

function Row({ k, v, muted }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className={`text-xs ${muted ? "text-slate-400" : "text-slate-300"}`}>{k}</div>
      <div className={`font-display font-semibold tabular ${muted ? "text-slate-300" : "text-white"}`}>{v}</div>
    </div>
  );
}
